import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Smartphone,
  Zap,
  ShieldCheck,
  Bell,
  Layers,
  Rocket,
  Lightbulb,
  Palette,
  Code2,
  Cloud,
  Star,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";
import {
  RevealSection,
  SplitText,
  MagneticButton,
  staggerContainer,
  staggerItem,
  useParallax,
  EASE,
} from "../components/motion/ScrollFX";

// ================= THEME =================
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#120A0C",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

// ================= DATA =================
const features = [
  { icon: Layers, title: "Cross Platform", desc: "Single codebase across iOS and Android using React Native & Flutter." },
  { icon: Zap, title: "High Performance", desc: "Optimized for speed, smooth animations and rock-solid stability." },
  { icon: ShieldCheck, title: "Secure Architecture", desc: "Enterprise-grade data protection built in from day one." },
];

const process = [
  { step: "Ideation", desc: "Requirement analysis & planning" },
  { step: "UI / UX", desc: "Wireframes & interactive design" },
  { step: "Development", desc: "Agile coding & integrations" },
  { step: "Launch & Support", desc: "Testing, release & updates" },
];

const techStack = ["React Native", "Flutter", "Firebase", "Node.js", "REST APIs"];

const stats = [
  { value: "100+", label: "Apps Delivered" },
  { value: "4.8★", label: "Average App Rating" },
  { value: "99.9%", label: "Crash-Free Rate" },
];

const capabilities = [
  { no: "01", icon: Lightbulb, title: "Product Strategy", desc: "Turning your idea into a scoped, buildable app roadmap from day one." },
  { no: "02", icon: Palette, title: "UI / UX Design", desc: "Native-feeling interfaces designed around real user behavior on mobile." },
  { no: "03", icon: Code2, title: "App Development", desc: "Clean, tested, cross-platform code that ships to both stores." },
  { no: "04", icon: Cloud, title: "Backend & APIs", desc: "Scalable backend services and integrations that keep your app fast." },
];

const capabilityAccents = ["#E63946", "#D9A441", "#E63946", "#D9A441"];

function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${center ? "mx-auto" : ""}`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

// ---------------------------------------------------------------------
// Hero background — SignalField: a scatter of rounded app-icon tiles
// gently floating at different depths, with concentric "ping" ripples
// (like a notification / connection signal) expanding outward from a
// few of them, plus drifting glow blobs. No card/device mock — this
// background carries the whole hero on its own, distinct from the
// Web Development page's falling code-rain.
// ---------------------------------------------------------------------
const iconTiles = [
  { x: 10, y: 22, size: 34, rot: -8, tint: theme.accent, depth: 1 },
  { x: 85, y: 16, size: 26, rot: 10, tint: theme.gold, depth: 0.6 },
  { x: 6, y: 68, size: 24, rot: 6, tint: theme.gold, depth: 0.8 },
  { x: 92, y: 62, size: 30, rot: -6, tint: theme.accent, depth: 1.1 },
  { x: 22, y: 88, size: 20, rot: 12, tint: "#FFFFFF", depth: 0.5 },
  { x: 74, y: 90, size: 22, rot: -10, tint: theme.accent, depth: 0.7 },
  { x: 48, y: 8, size: 18, rot: 8, tint: "#FFFFFF", depth: 0.4 },
];

const pings = [
  { x: 10, y: 22, delay: 0 },
  { x: 92, y: 62, delay: 1.6 },
  { x: 74, y: 90, delay: 3.2 },
];

function SignalField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <span className="hero-glow-m-a" />
      <span className="hero-glow-m-b" />

      {iconTiles.map((t, i) => (
        <div
          key={i}
          className="tile-drift absolute rounded-2xl flex items-center justify-center"
          style={{
            left: `${t.x}%`,
            top: `${t.y}%`,
            width: t.size,
            height: t.size,
            backgroundColor: `${t.tint}1F`,
            border: `1px solid ${t.tint}40`,
            transform: `rotate(${t.rot}deg)`,
            animationDuration: `${5 + t.depth * 2}s`,
            animationDelay: `${i * 0.4}s`,
          }}
        >
          <Smartphone size={t.size * 0.42} style={{ color: t.tint }} />
        </div>
      ))}

      {pings.map((p, i) => (
        <span
          key={i}
          className="ping-ring absolute rounded-full border"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            borderColor: theme.accent,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}

      <span className="hero-dots-m" />
      <span className="hero-vignette-m" />
    </div>
  );
}

export default function MobileApp() {
  const [activeCap, setActiveCap] = useState(0);
  const heroRef = useRef(null);
  const bgParallax = useParallax(heroRef, 40);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <style>{`
        .hero-glow-m-a {
          position: absolute;
          width: 42rem;
          height: 42rem;
          top: 50%;
          left: 50%;
          margin: -21rem 0 0 -21rem;
          border-radius: 9999px;
          filter: blur(90px);
          opacity: 0.35;
          background: radial-gradient(circle, ${theme.accent}, transparent 65%);
          animation: glowMA 24s ease-in-out infinite;
        }
        @keyframes glowMA {
          0%   { transform: translate(-9rem, 4rem) scale(1); }
          35%  { transform: translate(8rem, -3rem) scale(1.15); }
          70%  { transform: translate(2rem, -9rem) scale(0.9); }
          100% { transform: translate(-9rem, 4rem) scale(1); }
        }
        .hero-glow-m-b {
          position: absolute;
          width: 28rem;
          height: 28rem;
          top: 50%;
          left: 50%;
          margin: -14rem 0 0 -14rem;
          border-radius: 9999px;
          filter: blur(95px);
          opacity: 0.25;
          background: radial-gradient(circle, ${theme.gold}, transparent 65%);
          animation: glowMB 30s ease-in-out infinite;
        }
        @keyframes glowMB {
          0%   { transform: translate(7rem, -5rem) scale(1); }
          40%  { transform: translate(-8rem, 2rem) scale(0.85); }
          75%  { transform: translate(-1rem, 8rem) scale(1.1); }
          100% { transform: translate(7rem, -5rem) scale(1); }
        }

        .tile-drift {
          animation-name: tileDrift;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }
        @keyframes tileDrift {
          0%, 100% { transform: translateY(0) rotate(var(--r, 0deg)); }
          50% { transform: translateY(-14px) rotate(var(--r, 0deg)); }
        }

        .ping-ring {
          width: 10px;
          height: 10px;
          margin: -5px 0 0 -5px;
          animation: pingExpand 4.8s ease-out infinite;
        }
        @keyframes pingExpand {
          0%   { width: 10px; height: 10px; margin: -5px 0 0 -5px; opacity: 0.7; border-width: 2px; }
          100% { width: 160px; height: 160px; margin: -80px 0 0 -80px; opacity: 0; border-width: 1px; }
        }

        .hero-dots-m {
          position: absolute;
          inset: -10px;
          opacity: 0.05;
          background-image: radial-gradient(#FFFFFF 1px, transparent 1px);
          background-size: 30px 30px;
          animation: dotsDriftM 46s linear infinite;
        }
        @keyframes dotsDriftM {
          from { background-position: 0 0; }
          to { background-position: 300px -280px; }
        }

        .hero-vignette-m {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at center, transparent 25%, ${theme.heroBg} 85%);
        }

        .card-glow {
          pointer-events: none;
          position: absolute;
          top: -3rem;
          right: -3rem;
          width: 9rem;
          height: 9rem;
          border-radius: 9999px;
          filter: blur(30px);
          background: ${theme.accent};
          opacity: 0.08;
          transition: opacity 0.3s ease;
        }
        .group:hover .card-glow { opacity: 0.16; }
        .card-glow-dark {
          pointer-events: none;
          position: absolute;
          inset: 0;
          background:
            radial-gradient(circle at 15% 15%, rgba(230,57,70,0.22), transparent 55%),
            repeating-linear-gradient(45deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 2px, transparent 2px, transparent 20px);
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-glow-m-a, .hero-glow-m-b, .tile-drift, .ping-ring, .hero-dots-m { animation: none !important; }
        }
      `}</style>

      {/* ================= HERO — signal field bg, no card/device mock ================= */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pt-44 pb-28 md:pt-56 md:pb-40"
        style={{ backgroundColor: theme.heroBg }}
      >
        <motion.div style={{ y: bgParallax }} className="absolute inset-0">
          <SignalField />
        </motion.div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <Eyebrow light center>Mobile App Development</Eyebrow>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-[1.08] tracking-tight text-white">
              <SplitText text="Apps built for" className="text-white" />
              <br />
              <span style={{ color: theme.accent }}>scale, security</span>{" "}
              <SplitText text="& engagement" delay={0.25} className="text-white inline" as="span" />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: EASE }}
              className="mt-6 text-lg text-white/60 max-w-xl mx-auto"
            >
              High-performance Android & iOS apps designed for scale, security,
              and user engagement.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-wrap items-center justify-center gap-4"
            >
              <MagneticButton>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="group inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-md font-semibold hover:brightness-110 transition-all"
                >
                  Start Your App
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                <Link
                  to="/work"
                  data-cursor="hover"
                  className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-3.5 rounded-md font-medium hover:bg-white hover:text-[#221619] transition-colors"
                >
                  View Our Work
                </Link>
              </MagneticButton>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= INTRO ================= */}
      <section className="py-24" style={{ backgroundColor: theme.surface }}>
        <RevealSection className="max-w-4xl mx-auto px-6">
          <Eyebrow>What We Do</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: theme.ink }}>
            Apps That Users Love
          </h2>
          <p className="text-lg leading-relaxed" style={{ color: theme.inkMuted }}>
            We build intuitive, fast, and reliable mobile applications using
            modern frameworks and best practices. From idea to launch, we
            focus on performance, UX, and long-term maintainability.
          </p>
        </RevealSection>
      </section>

      {/* ================= CAPABILITIES (dark band, interactive spotlight) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBand }}>
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-0 right-0 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-[0.12] translate-x-1/3 -translate-y-1/3 transition-colors duration-500"
          style={{ backgroundColor: capabilityAccents[activeCap] }}
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <RevealSection className="max-w-2xl mb-16">
            <Eyebrow light>How We Build</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
              <SplitText text="From idea to" />{" "}
              <SplitText text="app store" delay={0.2} className="inline" as="span" />
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-white/60">
              A focused end-to-end process, from first sketch to a live app in
              users' hands.
            </p>
          </RevealSection>

          <RevealSection
            delay={0.1}
            className="grid md:grid-cols-[280px_1fr] gap-4 md:gap-0 rounded-3xl border border-white/10 overflow-hidden"
          >
            <div className="flex md:flex-col overflow-x-auto md:overflow-visible" style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
              {capabilities.map((item, i) => {
                const isActive = i === activeCap;
                return (
                  <button
                    key={item.no}
                    data-cursor="hover"
                    onClick={() => setActiveCap(i)}
                    onMouseEnter={() => setActiveCap(i)}
                    className="relative flex items-center gap-3 text-left px-6 py-5 shrink-0 w-full transition-colors duration-300"
                    style={{ backgroundColor: isActive ? "rgba(255,255,255,0.05)" : "transparent" }}
                  >
                    <span
                      className="absolute left-0 top-0 bottom-0 w-[3px] transition-transform duration-300 origin-left"
                      style={{ backgroundColor: capabilityAccents[i], transform: isActive ? "scaleX(1)" : "scaleX(0)" }}
                    />
                    <span
                      className="font-mono text-xs font-semibold transition-colors duration-300"
                      style={{ color: isActive ? capabilityAccents[i] : "rgba(255,255,255,0.35)" }}
                    >
                      {item.no}
                    </span>
                    <span
                      className="text-sm font-semibold whitespace-nowrap transition-colors duration-300"
                      style={{ color: isActive ? "#FFFFFF" : "rgba(255,255,255,0.55)" }}
                    >
                      {item.title}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="relative p-10 md:p-14 min-h-[280px] flex items-center" style={{ backgroundColor: "rgba(255,255,255,0.02)" }}>
              <span className="pointer-events-none absolute -right-10 -bottom-10 opacity-[0.06]" style={{ color: "#FFFFFF" }}>
                {(() => {
                  const ActiveIcon = capabilities[activeCap].icon;
                  return <ActiveIcon size={220} strokeWidth={1} />;
                })()}
              </span>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCap}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="relative z-10 max-w-lg"
                >
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${capabilityAccents[activeCap]}26`, color: capabilityAccents[activeCap] }}
                  >
                    {(() => {
                      const ActiveIcon = capabilities[activeCap].icon;
                      return <ActiveIcon size={26} />;
                    })()}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    {capabilities[activeCap].title}
                  </h3>
                  <p className="mt-4 text-white/60 leading-relaxed">
                    {capabilities[activeCap].desc}
                  </p>
                  <Link
                    to="/contact"
                    data-cursor="hover"
                    style={{ color: capabilityAccents[activeCap] }}
                    className="mt-6 inline-flex items-center gap-2 font-semibold hover:brightness-110 transition-all w-fit"
                  >
                    Get started
                    <ArrowUpRight size={16} />
                  </Link>
                </motion.div>
              </AnimatePresence>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ================= FEATURES (bento, light) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.surfaceAlt }}>
        <DotGrid />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <RevealSection className="text-center max-w-3xl mx-auto mb-16">
            <Eyebrow center>Core Features</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold" style={{ color: theme.ink }}>
              <SplitText text="Built to" /> <span style={{ color: theme.accent }}>perform</span>
            </h2>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid md:grid-cols-3 gap-6"
          >
            {features.map((item, i) => (
              <motion.div
                variants={staggerItem}
                key={item.title}
                data-cursor="hover"
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
                className="group relative overflow-hidden rounded-3xl p-8 border shadow-sm hover:shadow-2xl"
                style={{ backgroundColor: "#FFFFFF", borderColor: theme.border }}
              >
                <span className="card-glow" />
                <div
                  className="relative w-14 h-14 flex items-center justify-center rounded-xl mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                  style={{ backgroundColor: theme.surfaceAlt, color: theme.accent }}
                >
                  <item.icon size={24} />
                </div>
                <h3 className="relative text-xl font-semibold" style={{ color: theme.ink }}>
                  {item.title}
                </h3>
                <p className="relative mt-3 leading-relaxed" style={{ color: theme.inkMuted }}>
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= PROCESS (light) ================= */}
      <section className="py-24" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-6xl mx-auto px-6">
          <RevealSection className="text-center mb-14">
            <Eyebrow center>Our Approach</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-extrabold" style={{ color: theme.ink }}>
              App Development Process
            </h2>
          </RevealSection>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-6"
          >
            {process.map((item, i) => (
              <motion.div
                key={item.step}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                data-cursor="hover"
                className="relative p-6 rounded-2xl border shadow-sm text-center"
                style={{ backgroundColor: "#FFFFFF", borderColor: theme.border }}
              >
                <span
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold mb-4"
                  style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-semibold mb-2" style={{ color: theme.ink }}>{item.step}</h3>
                <p className="text-sm" style={{ color: theme.inkMuted }}>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= TECH STACK (dark) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBandDeep }}>
        <DotGrid light />
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <RevealSection className="text-center mb-14">
            <Eyebrow light center>Our Toolkit</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white">Technologies We Use</h2>
          </RevealSection>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center"
          >
            {techStack.map((tech) => (
              <motion.div
                key={tech}
                variants={staggerItem}
                whileHover={{ scale: 1.08, y: -4 }}
                data-cursor="hover"
                className="p-6 rounded-xl border font-semibold text-white transition-colors"
                style={{ backgroundColor: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.1)" }}
              >
                {tech}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= STATS ================= */}
      <section className="py-24" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-8 text-center">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="p-8 rounded-2xl border shadow-sm"
              style={{ backgroundColor: "#FFFFFF", borderColor: theme.border }}
            >
              <div className="flex items-center justify-center gap-2">
                {s.label.includes("Rating") && <Star size={22} style={{ color: theme.gold }} />}
                <h3 className="text-4xl font-extrabold" style={{ color: theme.accent }}>{s.value}</h3>
              </div>
              <p className="mt-2" style={{ color: theme.inkMuted }}>{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section
        className="relative py-28 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${theme.ctaBg} 0%, ${theme.ctaBgDeep} 100%)` }}
      >
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.14]"
          style={{ backgroundColor: theme.accent }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute -right-6 -top-10 text-[13rem] font-extrabold leading-none"
          style={{ color: "rgba(255,255,255,0.04)" }}
        >
          <Bell />
        </span>

        <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Eyebrow center light>Let's Get Building</Eyebrow>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
            <SplitText text="Ready to launch your" />
            <span className="block">
              <SplitText text="mobile" delay={0.15} /> <span style={{ color: theme.accent }}>app?</span>
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Let's build a powerful app that users love.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 px-9 py-4 rounded-md text-white font-semibold text-lg shadow-lg shadow-black/30 hover:brightness-110 transition-all"
              >
                Get Started
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}